import { Link } from '@tanstack/react-router';
import { brandOriginUrl } from '@/api';
import { casinoGameThumbUrl, casinoPlayPath, type CasinoGame } from '@/domain';
import { cn } from '@/lib/cn';
import { Icon } from '@/ui/common';

export interface GameTileProps {
  game: CasinoGame;
  categoryOverride?: string;
  favorited?: boolean;
  onToggleFavorite?: (game: CasinoGame) => void;
  showFavorite?: boolean;
  size?: 'md' | 'sm';
  /** Gold border + glow (Figma LiveTile). */
  tone?: 'default' | 'live';
  className?: string;
}

export function GameTile({
  game,
  categoryOverride,
  favorited = false,
  onToggleFavorite,
  showFavorite = false,
  size = 'md',
  tone = 'default',
  className,
}: GameTileProps) {
  const to = casinoPlayPath(game, categoryOverride);
  // Temporary: thumbs live on the brand site (`VITE_BRAND_ORIGIN`), not cAPI.
  const thumb = casinoGameThumbUrl(brandOriginUrl(), game);
  const isFavorite = favorited || game.FavoriteId != null;

  return (
    <div
      className={cn(
        'group relative',
        tone === 'live' && 'rounded-md shadow-[0_0_7px_rgba(251,191,36,0.55)]',
        className,
      )}
    >
      <Link
        to={to}
        className={cn(
          'relative block size-full overflow-hidden rounded-md bg-bg-elevated',
          /* Figma GameTile: image-only 3:4; live uses fixed outer size from parent. */
          tone !== 'live' && (size === 'md' ? 'aspect-[180/240]' : 'aspect-[111/148]'),
          tone === 'live' && 'border-[1.5px] border-solid border-status-warning',
        )}
      >
        <img
          src={thumb}
          alt={game.Name}
          loading="lazy"
          className="size-full object-cover"
          onError={(event) => {
            event.currentTarget.style.visibility = 'hidden';
          }}
        />
        {tone !== 'live' ? (
          <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 pb-2 pt-8">
            <span className="line-clamp-2 text-label-s text-text-on-action">{game.Name}</span>
          </span>
        ) : null}
      </Link>
      {showFavorite && onToggleFavorite ? (
        <button
          type="button"
          className={cn(
            'absolute right-2 top-2 flex size-8 items-center justify-center rounded-md bg-black/50 text-text-on-action',
            isFavorite && 'text-action-primary',
          )}
          aria-label={isFavorite ? 'Remove favourite' : 'Add favourite'}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onToggleFavorite(game);
          }}
        >
          <Icon name="star" size={16} />
        </button>
      ) : null}
    </div>
  );
}
